/**
 * ETA-Berechnung fuer das Optimierungs-Fortschrittsmodal (F-C, Zwergehuus-
 * Ledger 2026-08-17). Ein Produktionslauf ueber 4.914 Produkte (Gesamtdauer
 * 14:28 min) legte drei Messfehler der alten `throughputPerMin`/`etaSec`-
 * Ableitung offen:
 *
 * 1. 8-Sekunden-Anlauf-Freigabe bei winziger Anlaufrate: `elapsedSec > 8 &&
 *    completed >= 10` genuegte, um eine ETA zu zeigen — aber der Gate
 *    veroeffentlichte die Zahl, waehrend die Anlaufrate noch bei ~10/Min lag
 *    (ein einzelner frueher Batch, kein Regelbetrieb). Die Rate war also viel
 *    zu NIEDRIG, nicht zu hoch: 4.914 / 10 ≈ 491 — die ETA fiel dadurch um
 *    Faktor ~33 zu HOCH aus ("noch ~490 Min"), obwohl der Lauf am Ende
 *    14:28 min brauchte (siehe der erste Unit-Test unten, der genau diese
 *    Zehn-in-Zehn-Sekunden-Anlaufrate nachbildet).
 * 2. Seit-Start-Mittel statt Fensterrate: `completed / elapsedSec` verschleppt
 *    JEDE fruehe Delle oder Spitze bis zum Laufende. Ein Lauf, der in der
 *    ersten Minute langsam anlief und danach konstant schnell lief, zeigte
 *    noch nach 3 Minuten eine zu pessimistische ETA, weil die traege
 *    Anlaufminute weiter mitgerechnet wurde (Normalphase-Test unten: 12 Min
 *    Fensterrate gegen ca. 17 Min Seit-Start-Rate).
 * 3. Keine Tail-/Stillstands-Erkennung: nichts war gecacht — `etaSec` wurde
 *    bei jedem Render frisch aus dem Seit-Start-Mittel berechnet. Aber wenn
 *    die letzten Produkte lange anstanden (Retries, ein haengender Batch),
 *    blieb der REST winzig gegen ein Mittel, das noch vom schnellen
 *    Hauptteil des Laufs getragen wurde: bei 4.870 von 4.914 nach 800 s
 *    Laufzeit lag die Seit-Start-Rate bei 6,09/s, macht 44 / 6,09 ≈ 7 s —
 *    weit unter der 45-s-Schwelle. Mit jeder weiteren Stillstands-Sekunde
 *    sank die Rate zwar leicht (Zaehler steht, Nenner waechst), aber erst
 *    nach sehr langer Zeit ueber die Schwelle — "unter 1 Min" stand deshalb
 *    minutenlang, ohne dass sich etwas bewegte.
 *
 * Gegenmittel, in dieser Reihenfolge geprueft:
 *   - Anlauf (WARMUP): die ersten `ETA_WARMUP_SEC` Sekunden NACH Modal-Oeffnung
 *     zeigen bewusst KEINE Zahl. `observedSinceMs` ist die eigene Messspanne
 *     des Hooks, nicht `optimization_jobs.started_at` — ein spaet geoeffnetes
 *     Modal (Reload, zweiter Tab) darf nicht von einem Lauf profitieren, den
 *     es selbst nie beobachtet hat.
 *   - Gleitendes Fenster (`ETA_WINDOW_SEC`): die Rate kommt aus den letzten
 *     120 s Messwerten, nicht aus der Lebenszeit des Jobs. `ETA_MIN_WINDOW_SPAN_SEC`
 *     verhindert, dass zwei Punkte im Abstand von 2 s eine Rate vortaeuschen.
 *   - Tail-Erkennung: unter `ETA_TAIL_REMAINING_FRACTION` Rest UND mindestens
 *     `ETA_STAGNATION_SEC` ohne Fortschritt heisst "finishing" statt einer
 *     erfundenen Minutenzahl. Der Tail-Check laeuft VOR dem Warm-up-Gate, weil
 *     ein spaet geoeffnetes Modal in einem laufenden Tail nicht erst 60 s lang
 *     "berechne" sagen soll (siehe Testfall "Modal spaet geoeffnet" fuer den
 *     Warm-up-Fall und den Tail-Test fuer den Vorrang).
 *
 * Akzeptierter Trade-off bei sehr kurzen Laeufen: ein Lauf, der komplett
 * innerhalb der ersten `ETA_WARMUP_SEC` Sekunden durchlaeuft, zeigt die
 * gesamte Optimierphase ueber "berechne …" und loest erst ueber
 * `remaining === 0` -> `lessThanMin` auf (nicht ueber die Fensterrate). Das
 * ist spec-akzeptiert (siehe Task-4-Brief) — vermutlich die erste
 * "ETA haengt fest"-Meldung dazu, wenn niemand vorgewarnt ist.
 */

export interface EtaSample {
  t: number;
  completed: number;
}

export type EtaState =
  | { kind: 'calculating' }
  | { kind: 'minutes'; minutes: number }
  | { kind: 'lessThanMin' }
  | { kind: 'finishing' };

export const ETA_WARMUP_SEC = 60;
export const ETA_MIN_WINDOW_SPAN_SEC = 30;
export const ETA_WINDOW_SEC = 120;
export const ETA_STAGNATION_SEC = 30;
export const ETA_TAIL_REMAINING_FRACTION = 0.15;
export const ETA_LESS_THAN_MIN_SEC = 45;

export function computeEtaState(
  samples: EtaSample[],
  nowMs: number,
  total: number,
  observedSinceMs: number,
): EtaState {
  if (total <= 0 || samples.length < 2) return { kind: 'calculating' };
  const last = samples[samples.length - 1];
  const remaining = Math.max(0, total - last.completed);
  if (remaining === 0) return { kind: 'lessThanMin' };

  // Tail VOR dem Warm-up: die Stagnation setzt selbst >= STAGNATION_SEC
  // Beobachtung voraus; ein spaet geoeffnetes Modal soll im Tail nicht 60 s
  // lang "berechne" sagen.
  const lastProgressAt = lastProgressTimestamp(samples);
  const inTail = remaining / total <= ETA_TAIL_REMAINING_FRACTION;
  if (inTail && (nowMs - lastProgressAt) / 1000 >= ETA_STAGNATION_SEC) return { kind: 'finishing' };

  if ((nowMs - observedSinceMs) / 1000 < ETA_WARMUP_SEC) return { kind: 'calculating' };

  const rate = windowRate(samples, nowMs);
  if (rate === null) return { kind: 'calculating' };
  // Ueber den Hook unerreichbar (monotoner Zaehler: `completed` faellt nie
  // ohne einen vollen Reset, siehe useOptimizationEta.ts) — die Stagnations-
  // Pruefung oben (>= ETA_STAGNATION_SEC seit dem letzten Fortschritt) hat
  // einen inTail-Stillstand in der Praxis laengst als "finishing" erkannt,
  // bevor hier ueberhaupt ein Fenster mit rate<=0 zustande kommt. Bleibt als
  // Absicherung fuer direkte Aufrufer von `computeEtaState` (die einen
  // Ruecksprung liefern koennten) bzw. falls die Tail-Pruefung je umgestellt
  // wird.
  if (rate <= 0) return inTail ? { kind: 'finishing' } : { kind: 'calculating' };
  const etaSec = remaining / rate;
  if (etaSec <= ETA_LESS_THAN_MIN_SEC) return { kind: 'lessThanMin' };
  return { kind: 'minutes', minutes: Math.max(1, Math.round(etaSec / 60)) };
}

/** Letzter Zeitstempel, an dem `completed` gegenueber dem Vorgaenger stieg. */
function lastProgressTimestamp(samples: EtaSample[]): number {
  for (let i = samples.length - 1; i > 0; i--) {
    if (samples[i].completed > samples[i - 1].completed) return samples[i].t;
  }
  return samples[0].t;
}

/**
 * Gleitende Rate in Produkten/SEKUNDE ueber die letzten `ETA_WINDOW_SEC`, oder
 * `null` wenn zu wenig bzw. zu eng beieinanderliegende Messpunkte im Fenster
 * liegen (`ETA_MIN_WINDOW_SPAN_SEC`). Gemeinsame Grundlage fuer
 * `computeEtaState` (ETA) und `windowRatePerMin` (LivePulse) — beide muessen
 * strukturell dieselbe Rate benutzen, sonst driften ETA und angezeigter
 * Durchsatz auseinander (die `ratePerMin`-Prop des LivePulse behauptet genau
 * das, siehe OptimizationProgressModal.tsx). Anders als `windowRatePerMin`
 * gibt diese Funktion auch eine nicht-positive Rate zurueck (nur "kein
 * Fenster" wird zu `null`) — `computeEtaState` muss "kein Fenster" (immer
 * calculating) von "Fenster vorhanden, aber Rate <= 0" (finishing im Tail,
 * sonst calculating) unterscheiden koennen.
 */
function windowRate(samples: EtaSample[], nowMs: number): number | null {
  const win = samples.filter((s) => s.t >= nowMs - ETA_WINDOW_SEC * 1000);
  if (win.length < 2) return null;
  const spanSec = (win[win.length - 1].t - win[0].t) / 1000;
  if (spanSec < ETA_MIN_WINDOW_SPAN_SEC) return null;
  return (win[win.length - 1].completed - win[0].completed) / spanSec;
}

/**
 * Ehrlicher Ist-Durchsatz (Produkte/Min) aus dem gleitenden Fenster, fuer die
 * LivePulse-Anzeige. Getrennt von `computeEtaState`, weil der Live-Puls auch
 * dann eine Rate zeigen soll, wenn der Rest noch nicht in eine ETA uebersetzt
 * werden kann (z. B. waehrend des Warm-ups).
 */
export function windowRatePerMin(samples: EtaSample[], nowMs: number): number | null {
  const rate = windowRate(samples, nowMs);
  return rate !== null && rate > 0 ? rate * 60 : null;
}
